<template>
  <div class="contents-form d-flex flex-column h-100">
    <div class="contents-form-body">
      <!-- Prompt -->
      <b-form-group :label="$t('ia.contents.prompt')" label-for="contents-prompt" class="mb-2">
        <b-form-textarea
          id="contents-prompt"
          v-model="prompt"
          rows="4"
          max-rows="10"
          :maxlength="max_prompt_length"
          :placeholder="$t('ia.contents.promptPlaceholder')"
          :state="errors.prompt.length ? false : null"
        ></b-form-textarea>
        <div class="d-flex justify-content-between mt-25">
          <div>
            <div v-for="(error, index) in errors.prompt" :key="index" class="text-danger small">{{ error }}</div>
          </div>
          <small class="text-muted">{{ prompt.length }}/{{ max_prompt_length }}</small>
        </div>
      </b-form-group>

      <!-- References -->
      <references-picker
        v-model="references"
        :max="max_references"
        :errors="errors.references"
        class="mb-2"
      ></references-picker>

      <!-- Aspect ratio -->
      <b-form-group :label="$t('ia.contents.format')" class="mb-2">
        <div class="aspect-ratio-options">
          <button
            v-for="ratio in aspect_ratios"
            :key="ratio"
            type="button"
            class="aspect-ratio-option"
            :class="{ active: aspect_ratio === ratio }"
            @click="aspect_ratio = ratio"
          >
            <span class="aspect-ratio-shape" :style="ratioShapeStyle(ratio)"></span>
            <span>{{ ratio }}</span>
          </button>
        </div>
        <div v-for="(error, index) in errors.aspect_ratio" :key="index" class="text-danger small mt-25">{{ error }}</div>
        <b-alert :show="show_video_format_warning" variant="warning" class="mt-1 mb-0">
          <div class="alert-body">
            <feather-icon icon="InfoIcon" class="mr-50"></feather-icon>{{ $t('ia.contents.videoVerticalWarning') }}
          </div>
        </b-alert>
      </b-form-group>

      <!-- Plan -->
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h6 class="mb-0">{{ $t('ia.contents.plan') }}</h6>
        <small :class="total > max_total ? 'text-danger' : 'text-muted'">
          {{ $t('ia.contents.totalContents', { total, max: max_total }) }}
        </small>
      </div>
      <div v-if="loading_models" class="mb-1">
        <b-skeleton type="input" class="mb-50"></b-skeleton>
      </div>
      <template v-else>
        <plan-row
          v-for="(row, index) in plan"
          :key="row.key"
          :row="row"
          :models="models"
          :max_quantity="max_total"
          :removable="plan.length > 1"
          :errors="errors.plan_rows[index] || []"
          @update="updateRow(index, $event)"
          @remove="removeRow(index)"
        ></plan-row>
      </template>
      <div v-for="(error, index) in plan_errors" :key="`plan_${index}`" class="text-danger small mb-50">{{ error }}</div>
      <b-button variant="outline-primary" size="sm" :disabled="total >= max_total" @click="addRow">
        <feather-icon icon="PlusIcon" class="mr-25"></feather-icon>{{ $t('ia.contents.addRow') }}
      </b-button>

      <!-- General errors -->
      <b-alert :show="errors.no_credits" variant="danger" class="mt-2 mb-0">
        <div class="alert-body">{{ $t('ia.contents.noCredits') }}</div>
      </b-alert>
      <b-alert :show="errors.general.length > 0" variant="danger" class="mt-2 mb-0">
        <div class="alert-body">
          <div v-for="(error, index) in errors.general" :key="index">{{ error }}</div>
        </div>
      </b-alert>
    </div>

    <!-- Cost and generate -->
    <div class="contents-form-footer">
      <cost-summary
        :credits_estimated="estimate.credits_estimated"
        :credits_available="credits_available"
        :loading="loading_estimate"
      ></cost-summary>
      <b-button class="generate-contents-ai" :disabled="!can_generate" @click="createRequest">
        <b-spinner v-if="creating" small class="mr-50"></b-spinner>
        {{ $t('ia.contents.generate') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormTextarea,
  BButton,
  BAlert,
  BSkeleton,
  BSpinner,
} from "bootstrap-vue";
import { getContentModels, estimateContentRequest, createContentRequest } from '@/services/ai-contents';
import { showToast } from '@/libs/utils/others';
import {
  MAX_PROMPT_LENGTH,
  MAX_TOTAL_CONTENTS,
  ASPECT_RATIOS,
  DEFAULT_ASPECT_RATIO,
  VIDEO_ASPECT_RATIOS,
  newPlanRow,
  defaultModelKey,
  maxReferences,
  totalContents,
  buildRequestPayload,
  parseRequestErrors,
} from './contents-utils';

const ESTIMATE_DEBOUNCE = 600;

const emptyErrors = () => ({ general: [], prompt: [], aspect_ratio: [], plan: [], references: [], plan_rows: {}, no_credits: false });

export default {
  name: 'ContentsForm',
  components: {
    BFormGroup,
    BFormTextarea,
    BButton,
    BAlert,
    BSkeleton,
    BSpinner,
    PlanRow: () => import('./PlanRow.vue'),
    CostSummary: () => import('./CostSummary.vue'),
    ReferencesPicker: () => import('./ReferencesPicker.vue'),
  },
  props: {
    // { type: 'campaign' | 'casting_call', uuid } or null for standalone BrandMeAI
    source: {
      type: Object,
      default: null,
    },
    credits: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      max_prompt_length: MAX_PROMPT_LENGTH,
      max_total: MAX_TOTAL_CONTENTS,
      aspect_ratios: ASPECT_RATIOS,
      prompt: '',
      aspect_ratio: DEFAULT_ASPECT_RATIO,
      plan: [newPlanRow('image')],
      references: [],
      models: [],
      loading_models: true,
      estimate: { credits_estimated: null, credits_available: null },
      loading_estimate: false,
      estimate_timer: null,
      estimate_sequence: 0,
      errors: emptyErrors(),
      creating: false,
    };
  },
  created() {
    this.getModels();
  },
  beforeDestroy() {
    clearTimeout(this.estimate_timer);
  },
  computed: {
    total() {
      return totalContents(this.plan);
    },
    is_valid() {
      return this.prompt.trim().length > 0 && this.total > 0 && this.total <= this.max_total &&
        this.plan.every((row) => parseInt(row.quantity) >= 1);
    },
    payload() {
      return buildRequestPayload(
        { prompt: this.prompt, aspect_ratio: this.aspect_ratio, plan: this.plan, references: this.references },
        this.models,
        this.$i18n.locale,
        this.source,
      );
    },
    max_references() {
      return maxReferences(this.models, this.plan);
    },
    payload_signature() {
      return JSON.stringify(this.payload);
    },
    credits_available() {
      return this.estimate.credits_available !== null ? this.estimate.credits_available : this.credits;
    },
    insufficient_credits() {
      return this.credits_available !== -1 && this.estimate.credits_estimated !== null &&
        this.credits_available !== null && this.estimate.credits_estimated > this.credits_available;
    },
    has_errors() {
      const errors = this.errors;
      return errors.no_credits || errors.general.length > 0 || errors.prompt.length > 0 ||
        errors.aspect_ratio.length > 0 || errors.plan.length > 0 || errors.references.length > 0 ||
        Object.keys(errors.plan_rows).length > 0;
    },
    can_generate() {
      return this.is_valid && !this.loading_models && !this.loading_estimate && !this.creating &&
        this.estimate.credits_estimated !== null && !this.insufficient_credits && !this.has_errors;
    },
    show_video_format_warning() {
      return !VIDEO_ASPECT_RATIOS.includes(this.aspect_ratio) && this.plan.some((row) => row.asset_type === 'video');
    },
    plan_errors() {
      const errors = [...this.errors.plan];
      if (this.total > this.max_total) errors.push(this.$t('ia.contents.maxContentsError', { max: this.max_total }));
      return errors;
    },
  },
  watch: {
    payload_signature() {
      this.scheduleEstimate();
    },
    max_references(max) {
      if (this.references.length > max) this.references = this.references.slice(0, max);
    },
  },
  methods: {
    getModels() {
      this.loading_models = true;
      getContentModels().then((response) => {
        this.models = Array.isArray(response) ? response : (response.results || []);
        this.plan = this.plan.map((row) => (row.model_key ? row : { ...row, model_key: defaultModelKey(this.models, row.asset_type) }));
      }).catch(() => {
        showToast(this, 'danger', 'AlertCircleIcon', 'ia.contents.errorModels');
      }).finally(() => {
        this.loading_models = false;
      });
    },
    scheduleEstimate() {
      clearTimeout(this.estimate_timer);
      this.errors = emptyErrors();
      this.estimate.credits_estimated = null;
      this.estimate_sequence += 1;

      if (!this.is_valid) {
        this.loading_estimate = false;
        return;
      }
      this.loading_estimate = true;
      this.estimate_timer = setTimeout(this.getEstimate, ESTIMATE_DEBOUNCE);
    },
    getEstimate() {
      const sequence = this.estimate_sequence;
      estimateContentRequest(this.payload).then((response) => {
        if (sequence !== this.estimate_sequence) return;
        this.estimate = {
          credits_estimated: response.credits_estimated,
          credits_available: response.credits_available,
        };
      }).catch((error) => {
        if (sequence !== this.estimate_sequence) return;
        this.errors = parseRequestErrors(error);
      }).finally(() => {
        if (sequence === this.estimate_sequence) this.loading_estimate = false;
      });
    },
    createRequest() {
      if (!this.can_generate) return;
      this.creating = true;
      createContentRequest(this.payload).then((response) => {
        this.$emit('created', response);
      }).catch((error) => {
        if (error.status === 400) this.errors = parseRequestErrors(error);
        else showToast(this, 'danger', 'AlertCircleIcon', 'ia.contents.errorCreate');
      }).finally(() => {
        this.creating = false;
      });
    },
    addRow() {
      this.plan.push({ ...newPlanRow('image'), model_key: defaultModelKey(this.models, 'image') });
    },
    removeRow(index) {
      this.plan.splice(index, 1);
    },
    updateRow(index, row) {
      this.plan.splice(index, 1, row);
    },
    ratioShapeStyle(ratio) {
      const [width, height] = ratio.split(':').map(Number);
      const scale = 22 / Math.max(width, height);
      return { width: `${width * scale}px`, height: `${height * scale}px` };
    },
  },
};
</script>

<style lang="scss" scoped>
.contents-form-body {
  flex-grow: 1;
}
.aspect-ratio-options {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;

  .aspect-ratio-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 0.4rem;
    width: 72px;
    height: 72px;
    padding: 0.5rem;
    background-color: #fff;
    border: 1px solid #d8d6de;
    border-radius: 0.5rem;
    color: #6e6b7b;
    cursor: pointer;

    &:hover {
      background-color: #f8f8f8;
    }

    &.active {
      border-color: #7367f0;
      color: #7367f0;
      box-shadow: 0 0 0 1px #7367f0;

      .aspect-ratio-shape {
        border-color: #7367f0;
      }
    }

    .aspect-ratio-shape {
      border: 2px solid #b9b9c3;
      border-radius: 3px;
    }
  }
}
.contents-form-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  padding: 1rem 0;
  background-color: #fff;
  border-top: 1px solid #ebe9f1;

  .generate-contents-ai {
    height: 42px;
    display: flex;
    align-items: center;
    border: 0;
    background: linear-gradient(to left bottom, rgb(124, 214, 175), rgb(62, 123, 160));

    &:disabled {
      background: #b9b9c3;
    }
  }
}
</style>
